import { FC, FormEvent, useState } from 'react';

import { router, useForm, usePage } from '@inertiajs/react';
import {
    ArrowDownRight,
    ArrowUpRight,
    CircleDollarSign,
    TriangleAlert,
} from 'lucide-react';

import { ServiceFeeInformationCard } from './ServiceFeeInformationCard';
import { InfiniteScrollTrigger } from '../../../components/shared/InfiniteScrollTrigger';
import { ServiceFeeBalanceStatus } from '../../../components/shared/serviceFeePresentation';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../../components/ui/Card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/Dialog';
import { Input } from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import { Routes } from '../../../models/routes';
import {
    formatBalanceWithPendingFees,
    formatLocalDateTime,
} from '../../../utils';

interface BalanceTransaction {
    id: number;
    amount_cents: number;
    transaction_type:
        | 'deposit'
        | 'deposit_reversal'
        | 'service_fee'
        | 'adjustment';
    description: string;
    created_at: string;
    order_number?: string | null;
}

interface PageProps extends Record<string, unknown> {
    balanceCents: number;
    balanceDebtCents: number;
    serviceFeeBalanceStatus: ServiceFeeBalanceStatus;
    transactions: BalanceTransaction[];
    pagination: {
        currentPage: number;
        nextPage: number | null;
        previousPage: number | null;
        pages: number;
    };
}

const formatCents = (amountCents: number) =>
    new Intl.NumberFormat('uk-UA', {
        style: 'currency',
        currency: 'UAH',
        minimumFractionDigits: 2,
    }).format(amountCents / 100);

const transactionLabel = (transaction: BalanceTransaction) => {
    if (transaction.transaction_type === 'deposit') return 'Поповнення балансу';
    if (transaction.transaction_type === 'deposit_reversal') {
        return 'Скасування поповнення балансу';
    }
    if (transaction.transaction_type === 'service_fee') {
        return transaction.order_number
            ? `Сервісний збір за замовлення #${transaction.order_number}`
            : transaction.description;
    }

    return transaction.description;
};

const Balance: FC = () => {
    const {
        balanceCents,
        balanceDebtCents,
        serviceFeeBalanceStatus,
        transactions,
        pagination,
    } = usePage<PageProps>().props;
    const { url } = usePage();
    const [depositOpen, setDepositOpen] = useState(
        new URLSearchParams(url.split('?')[1] ?? '').get('deposit') === '1',
    );
    const [loadingMore, setLoadingMore] = useState(false);
    const { data, setData, post, processing, errors, reset } = useForm({
        amount: '',
    });

    const submitDeposit = (event: FormEvent) => {
        event.preventDefault();
        post(Routes.BalanceDeposits, {
            onSuccess: () => {
                setDepositOpen(false);
                reset();
            },
        });
    };

    const loadMore = () => {
        if (!pagination.nextPage || loadingMore) return;

        router.get(
            Routes.Balance,
            { page: pagination.nextPage },
            {
                preserveScroll: true,
                preserveState: true,
                preserveUrl: true,
                only: ['transactions', 'pagination'],
                onStart: () => setLoadingMore(true),
                onFinish: () => setLoadingMore(false),
            },
        );
    };

    return (
        <div className="space-y-6 py-6">
            <div>
                <h1 className="text-2xl font-bold sm:text-3xl">Баланс</h1>
            </div>

            {balanceDebtCents > 0 && (
                <Alert variant="destructive">
                    <TriangleAlert />
                    <AlertTitle>
                        Скасоване банком поповнення:{' '}
                        {formatCents(balanceDebtCents)}
                    </AlertTitle>
                    <AlertDescription>
                        Наступні поповнення спочатку буде спрямовано на
                        погашення цієї суми.
                    </AlertDescription>
                </Alert>
            )}

            <Card>
                <CardHeader>
                    <CardDescription>Поточний баланс</CardDescription>
                    <CardTitle className="flex items-center gap-3 text-4xl">
                        <CircleDollarSign className="h-8 w-8 text-primary" />
                        {formatBalanceWithPendingFees(
                            balanceCents,
                            serviceFeeBalanceStatus.pendingServiceFeesCents,
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Button onClick={() => setDepositOpen(true)}>
                        Поповнити баланс
                    </Button>
                </CardContent>
            </Card>

            <ServiceFeeInformationCard />

            <Card>
                <CardHeader>
                    <CardTitle className="text-xl">Історія операцій</CardTitle>
                </CardHeader>
                <CardContent>
                    {transactions.length === 0 ? (
                        <p className="py-8 text-center text-sm text-muted-foreground">
                            Операцій поки немає.
                        </p>
                    ) : (
                        <div className="divide-y">
                            {transactions.map((transaction) => {
                                const positive = transaction.amount_cents > 0;
                                const Icon = positive
                                    ? ArrowUpRight
                                    : ArrowDownRight;
                                const label = transactionLabel(transaction);

                                return (
                                    <div
                                        key={transaction.id}
                                        className="flex items-center gap-3 py-4"
                                    >
                                        <div className="rounded-full bg-muted p-2">
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium">
                                                {label}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {formatLocalDateTime(
                                                    transaction.created_at,
                                                )}
                                            </p>
                                        </div>
                                        <Badge
                                            variant={
                                                positive
                                                    ? 'success'
                                                    : 'secondary'
                                            }
                                            className="text-sm"
                                        >
                                            {positive ? '+' : ''}
                                            {formatCents(
                                                transaction.amount_cents,
                                            )}
                                        </Badge>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {pagination.nextPage && (
                        <InfiniteScrollTrigger
                            className="mt-4 border-t pt-4"
                            loading={loadingMore}
                            onLoadMore={loadMore}
                        />
                    )}
                </CardContent>
            </Card>

            <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
                <DialogContent className="max-w-md">
                    <form onSubmit={submitDeposit} className="space-y-5">
                        <DialogHeader>
                            <DialogTitle>Поповнити баланс</DialogTitle>
                            <DialogDescription>
                                Кошти можна використати лише для сервісних
                                зборів CartOn.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-2">
                            <Label htmlFor="deposit-amount">
                                Сума поповнення
                            </Label>
                            <div className="relative">
                                <Input
                                    id="deposit-amount"
                                    type="number"
                                    min="1"
                                    max="100000"
                                    step="0.01"
                                    inputMode="decimal"
                                    value={data.amount}
                                    onChange={(event) =>
                                        setData('amount', event.target.value)
                                    }
                                    className="pr-12"
                                    autoFocus
                                />
                                <span className="absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted-foreground">
                                    грн
                                </span>
                            </div>
                            {errors.amount && (
                                <p className="text-sm text-destructive">
                                    {errors.amount}
                                </p>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                            {[100, 200, 500, 1000].map((amount) => (
                                <Button
                                    key={amount}
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setData('amount', amount.toString())
                                    }
                                >
                                    +{amount} ₴
                                </Button>
                            ))}
                        </div>

                        <DialogFooter>
                            <Button type="submit" disabled={processing}>
                                {processing
                                    ? 'Створюємо платіж…'
                                    : 'Перейти до оплати'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Balance;
